import { useEffect } from "react";

/**
 * Хук для обработки жестов свайпов и тапов
 * @param {Object} options - Настройки жестов
 * @param {Object} options.elementRef - Ref на элемент для прослушивания событий
 * @param {Function} options.onGoLeft - Callback для движения влево/назад
 * @param {Function} options.onGoRight - Callback для движения вправо/вперед
 * @param {Function} options.onUIToggle - Callback для переключения UI
 * @param {Object} options.settings - Настройки порогов и зон
 */
export default function useSwipeGestures({
  elementRef,
  onGoLeft,
  onGoRight,
  onUIToggle,
  settings = {},
}) {
  // Настройки по умолчанию
  const {
    minSwipeDistance = 50,
    maxSwipeTime = 300,
    minSwipeSpeed = 0.1,
    maxTapDistance = 10,
    maxTapTime = 300,
    leftZonePercent = 0.25,
    rightZonePercent = 0.75,
    touchMouseDelay = 500,
  } = settings;

  useEffect(() => {
    if (!elementRef.current) return;

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let lastTouchTime = 0; // Для предотвращения двойного срабатывания

    const handleStart = (event) => {
      // Предотвращаем mouse события если недавно был touch
      if (
        event.type === "mousedown" &&
        Date.now() - lastTouchTime < touchMouseDelay
      ) {
        return;
      }

      const touch = event.touches ? event.touches[0] : event;
      startX = touch.clientX;
      startY = touch.clientY;
      startTime = Date.now();
    };

    const handleEnd = (event) => {
      // Предотвращаем mouse события если недавно был touch
      if (
        event.type === "mouseup" &&
        Date.now() - lastTouchTime < touchMouseDelay
      ) {
        return;
      }

      if (startX === undefined || startY === undefined) return;

      const touch = event.changedTouches ? event.changedTouches[0] : event;
      const endX = touch.clientX;
      const endY = touch.clientY;
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const deltaTime = Date.now() - startTime;

      // Запоминаем время touch события
      if (event.type === "touchend") {
        lastTouchTime = Date.now();
      }

      // Проверяем что это быстрый горизонтальный свайп
      const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
      const isLongEnough = Math.abs(deltaX) > minSwipeDistance;
      const isFastEnough = deltaTime < maxSwipeTime;
      const isMinSpeed = Math.abs(deltaX) / deltaTime > minSwipeSpeed;

      if (isHorizontalSwipe && isLongEnough && (isFastEnough || isMinSpeed)) {
        event.preventDefault();
        if (deltaX > 0 && onGoLeft) {
          onGoLeft();
        } else if (deltaX < 0 && onGoRight) {
          onGoRight();
        }
      } else if (
        Math.abs(deltaX) < maxTapDistance &&
        Math.abs(deltaY) < maxTapDistance &&
        deltaTime < maxTapTime
      ) {
        // Определяем зону тапа для навигации по страницам
        const screenWidth = window.innerWidth;
        const tapX = endX;
        const leftZone = screenWidth * leftZonePercent;
        const rightZone = screenWidth * rightZonePercent;

        if (tapX < leftZone && onGoLeft) {
          // Тап в левой зоне - предыдущая страница
          onGoLeft();
          return;
        } else if (tapX > rightZone && onGoRight) {
          // Тап в правой зоне - следующая страница
          onGoRight();
          return;
        }

        // Тап в центральной зоне - переключение UI
        if (onUIToggle) {
          onUIToggle();
        }
      }

      // Сбрасываем значения
      startX = 0;
      startY = 0;
      startTime = 0;
    };

    const element = elementRef.current;
    element.addEventListener("touchstart", handleStart, { passive: false });
    element.addEventListener("touchend", handleEnd, { passive: false });
    element.addEventListener("mousedown", handleStart);
    element.addEventListener("mouseup", handleEnd);

    return () => {
      element.removeEventListener("touchstart", handleStart);
      element.removeEventListener("touchend", handleEnd);
      element.removeEventListener("mousedown", handleStart);
      element.removeEventListener("mouseup", handleEnd);
    };
  }, [
    elementRef,
    onGoLeft,
    onGoRight,
    onUIToggle,
    minSwipeDistance,
    maxSwipeTime,
    minSwipeSpeed,
    maxTapDistance,
    maxTapTime,
    leftZonePercent,
    rightZonePercent,
    touchMouseDelay,
  ]);
}
